import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Handshake, Plus, ArrowRight, Zap, Trophy, XCircle, Pause,
  RefreshCw, ChevronRight, IndianRupee, Trash2, Pencil, AlertTriangle, ShieldCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { usePermissions } from "../hooks/usePermissions";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

const STATUS_STYLES = {
  active: "bg-blue-100 text-blue-700", won: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-700", stalled: "bg-amber-100 text-amber-700",
};

const FREEBIES = ["Parking", "Modular Kitchen", "Stamp Duty Waiver", "Registration Waiver", "Club Membership", "Floor Rise Waiver", "GST Absorbed", "Maintenance Free (1yr)", "White Goods", "AC Units"];

const NegotiationPage = () => {
  const { canEdit } = usePermissions();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ lead_id: "", project_name: "", unit_id: "", payment_plan: "", initial_quoted_price: "", offered_price: "", freebies: [], notes: "" });
  const [leadSearch, setLeadSearch] = useState("");
  const [leadResults, setLeadResults] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  const [roundNeg, setRoundNeg] = useState(null);
  const [roundForm, setRoundForm] = useState({ offered_price: "", freebies: [], notes: "" });
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNeg, setCloseNeg] = useState(null);
  const [closeOutcome, setCloseOutcome] = useState("");
  const [closeFinalPrice, setCloseFinalPrice] = useState("");
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailNeg, setDetailNeg] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalNeg, setApprovalNeg] = useState(null);
  const [approvalJustification, setApprovalJustification] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/negotiations`);
      setNegotiations(res.data);
    } catch { toast.error("Failed to load negotiations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Search leads for create negotiation
  const searchLeadsForNeg = async (query) => {
    if (!query || query.length < 2) {
      setLeadResults([]);
      return;
    }
    setSearchingLeads(true);
    try {
      const res = await axios.get(`${API}/leads`, { params: { search: query, limit: 10 } });
      setLeadResults(res.data.leads || []);
    } catch (e) {
      setLeadResults([]);
    } finally {
      setSearchingLeads(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (leadSearch) searchLeadsForNeg(leadSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [leadSearch]);

  const selectLeadForNeg = (lead) => {
    setSelectedLead(lead);
    setForm({ ...form, lead_id: lead.lead_id, project_name: lead.project_interest || "" });
    setLeadSearch("");
    setLeadResults([]);
  };

  const clearSelectedLeadForNeg = () => {
    setSelectedLead(null);
    setForm({ ...form, lead_id: "" });
  };

  const saveEditNeg = async () => {
    if (!editForm) return;
    try {
      await axios.put(`${API}/negotiations/${editForm.negotiation_id}`, {
        payment_plan: editForm.payment_plan,
        notes: editForm.notes,
        project_name: editForm.project_name,
      });
      toast.success("Negotiation updated");
      setEditOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const createNeg = async () => {
    try {
      await axios.post(`${API}/negotiations`, {
        ...form,
        initial_quoted_price: Number(form.initial_quoted_price),
        offered_price: Number(form.offered_price) || Number(form.initial_quoted_price),
      });
      toast.success("Negotiation started");
      setCreateOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addRound = async () => {
    try {
      await axios.post(`${API}/negotiations/${roundNeg.negotiation_id}/round`, {
        ...roundForm, offered_price: Number(roundForm.offered_price),
      });
      toast.success("Round added");
      setRoundOpen(false);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const closeNegotiation = async () => {
    try {
      await axios.post(`${API}/negotiations/${closeNeg.negotiation_id}/close`, {
        outcome: closeOutcome, final_price: Number(closeFinalPrice) || undefined,
      });
      toast.success(`Negotiation ${closeOutcome}`);
      setCloseOpen(false);
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const getStrategy = async (neg) => {
    setStrategyLoading(true); setStrategyOpen(true); setStrategy(null);
    try {
      const res = await axios.post(`${API}/negotiations/${neg.negotiation_id}/ai-strategy`);
      setStrategy(res.data.strategy);
    } catch { toast.error("AI strategy failed"); setStrategyOpen(false); }
    finally { setStrategyLoading(false); }
  };

  const viewDetail = async (negId) => {
    try {
      const res = await axios.get(`${API}/negotiations/${negId}`);
      setDetailNeg(res.data);
      setDetailOpen(true);
    } catch { toast.error("Failed to load details"); }
  };

  const requestDiscountApproval = async () => {
    if (!approvalNeg) return;
    try {
      await axios.post(`${API}/discount-approval/requests`, {
        negotiation_id: approvalNeg.negotiation_id,
        original_price: approvalNeg.initial_quoted_price,
        discounted_price: approvalNeg.current_offered_price,
        freebies: approvalNeg.freebies || [],
        justification: approvalJustification
      });
      toast.success("Discount approval request submitted");
      setApprovalOpen(false);
      setApprovalJustification("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit approval request");
    }
  };

  const filtered = filterStatus === "all" ? negotiations : negotiations.filter(n => n.status === filterStatus);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="negotiation-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Negotiations</h1>
            <p className="text-sm text-slate-500 mt-1">Track deals — offers, counter-offers, freebies, AI strategy</p>
          </div>
          <Button onClick={() => { setForm({ lead_id: "", project_name: "", unit_id: "", payment_plan: "", initial_quoted_price: "", offered_price: "", freebies: [], notes: "" }); setCreateOpen(true); }} data-testid="create-neg-btn">
            <Plus className="w-4 h-4 mr-1" /> New Negotiation
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Handshake className="w-5 h-5 text-blue-700" /></div><div><p className="text-xs text-slate-500">Active</p><p className="text-lg font-bold">{negotiations.filter(n => n.status === "active").length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Trophy className="w-5 h-5 text-emerald-700" /></div><div><p className="text-xs text-slate-500">Won</p><p className="text-lg font-bold text-emerald-600">{negotiations.filter(n => n.status === "won").length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-700" /></div><div><p className="text-xs text-slate-500">Lost</p><p className="text-lg font-bold text-red-600">{negotiations.filter(n => n.status === "lost").length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-amber-700" /></div><div><p className="text-xs text-slate-500">Pipeline Value</p><p className="text-lg font-bold">{formatINR(negotiations.filter(n => n.status === "active").reduce((s, n) => s + (n.current_offered_price || 0), 0))}</p></div></CardContent></Card>
        </div>

        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="stalled">Stalled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <div className="text-center py-12 text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /></div> :
        filtered.length === 0 ? <Card><CardContent className="p-12 text-center text-slate-400"><Handshake className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No negotiations yet</p></CardContent></Card> : (
          <div className="space-y-2">
            {filtered.map(n => (
              <Card key={n.negotiation_id} className="hover:shadow-md transition-shadow" data-testid={`neg-card-${n.negotiation_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{n.lead_name}</span>
                        <Badge className={`text-xs ${STATUS_STYLES[n.status]}`}>{n.status}</Badge>
                        <Badge variant="outline" className="text-xs">{n.payment_plan || "N/A"}</Badge>
                        <span className="text-xs text-slate-400">{n.rounds?.length || 0} round(s)</span>
                        {n.pending_approval && (
                          <Badge className="text-xs bg-amber-100 text-amber-700">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Pending Approval
                          </Badge>
                        )}
                        {n.discount_approved && (
                          <Badge className="text-xs bg-emerald-100 text-emerald-700">
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span>Project: {n.project_name || "N/A"}</span>
                        <span>Quoted: {formatINR(n.initial_quoted_price)}</span>
                        <span className="font-medium text-slate-700">Current: {formatINR(n.current_offered_price)}</span>
                        {n.total_discount_pct > 0 && <span className="text-red-500">-{n.total_discount_pct}%</span>}
                        {n.freebies?.length > 0 && <span className="text-emerald-600">+{n.freebies.length} freebies</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="outline" size="sm" className="text-xs" onClick={() => viewDetail(n.negotiation_id)}>View</Button>
                      {n.status === "active" && (
                        <>
                          {canEdit("edit_negotiations") && (
                            <Button variant="ghost" size="sm" className="text-xs h-7 w-7 p-0" onClick={() => { setEditForm({...n}); setEditOpen(true); }} data-testid={`edit-neg-${n.negotiation_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                          )}
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => { setRoundNeg(n); setRoundForm({ offered_price: "", freebies: [], notes: "" }); setRoundOpen(true); }} data-testid={`add-round-${n.negotiation_id}`}>+ Round</Button>
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => getStrategy(n)} data-testid={`ai-strategy-${n.negotiation_id}`}><Zap className="w-3 h-3 mr-1" />AI</Button>
                          {n.total_discount_pct > 0 && !n.pending_approval && !n.discount_approved && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={() => { setApprovalNeg(n); setApprovalJustification(""); setApprovalOpen(true); }}
                              data-testid={`request-approval-${n.negotiation_id}`}
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" />Approval
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => { setCloseNeg(n); setCloseOutcome(""); setCloseFinalPrice(""); setCloseOpen(true); }}>Close</Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={(open) => { 
          setCreateOpen(open); 
          if (!open) { 
            setSelectedLead(null); 
            setLeadSearch(""); 
            setLeadResults([]); 
          } 
        }}>
          <DialogContent className="sm:max-w-md" data-testid="create-neg-dialog">
            <DialogHeader><DialogTitle>Start Negotiation</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Select Lead *</label>
                {selectedLead ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{selectedLead.name}</p>
                      <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                        <span>{selectedLead.phone}</span>
                        {selectedLead.email && <span>{selectedLead.email}</span>}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{selectedLead.project_interest || "No project"}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 h-8" onClick={clearSelectedLeadForNeg}>Change</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input 
                      value={leadSearch} 
                      onChange={(e) => setLeadSearch(e.target.value)} 
                      placeholder="Search by name, phone, email..." 
                      data-testid="neg-lead-search" 
                    />
                    {(leadResults.length > 0 || searchingLeads) && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {searchingLeads ? (
                          <div className="p-3 text-center text-slate-400 text-sm">Searching...</div>
                        ) : (
                          leadResults.map(lead => (
                            <div 
                              key={lead.lead_id}
                              className="p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => selectLeadForNeg(lead)}
                            >
                              <p className="font-medium text-sm">{lead.name}</p>
                              <p className="text-xs text-slate-500">{lead.phone} • {lead.project_interest || "No project"}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div><label className="text-sm font-medium mb-1 block">Project</label><Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Unit ID (optional)</label><Input value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Payment Plan</label>
                <Select value={form.payment_plan} onValueChange={(v) => setForm({ ...form, payment_plan: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLP">CLP (Construction Linked)</SelectItem>
                    <SelectItem value="TLP">TLP (Time Linked)</SelectItem>
                    <SelectItem value="down_payment">Down Payment</SelectItem>
                    <SelectItem value="flexi">Flexi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-sm font-medium mb-1 block">Quoted Price</label><Input type="number" value={form.initial_quoted_price} onChange={(e) => setForm({ ...form, initial_quoted_price: e.target.value })} data-testid="neg-quoted-price" /></div>
                <div><label className="text-sm font-medium mb-1 block">Buyer's Offer</label><Input type="number" value={form.offered_price} onChange={(e) => setForm({ ...form, offered_price: e.target.value })} /></div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={createNeg} disabled={!form.lead_id} data-testid="neg-save-btn">Start</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Round Dialog */}
        <Dialog open={roundOpen} onOpenChange={setRoundOpen}>
          <DialogContent className="sm:max-w-md" data-testid="round-dialog">
            <DialogHeader><DialogTitle>Add Negotiation Round — {roundNeg?.lead_name}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <p className="text-xs text-slate-500">Current offer: {formatINR(roundNeg?.current_offered_price)} | Discount: {roundNeg?.total_discount_pct}%</p>
              <div><label className="text-sm font-medium mb-1 block">New Offered Price</label><Input type="number" value={roundForm.offered_price} onChange={(e) => setRoundForm({ ...roundForm, offered_price: e.target.value })} data-testid="round-price" /></div>
              <div>
                <label className="text-sm font-medium mb-1 block">Freebies</label>
                <div className="flex flex-wrap gap-1">
                  {FREEBIES.map(f => (
                    <button key={f} className={`text-xs px-2 py-1 rounded-full border transition-all ${roundForm.freebies.includes(f) ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                      onClick={() => setRoundForm({ ...roundForm, freebies: roundForm.freebies.includes(f) ? roundForm.freebies.filter(x => x !== f) : [...roundForm.freebies, f] })}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={roundForm.notes} onChange={(e) => setRoundForm({ ...roundForm, notes: e.target.value })} rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoundOpen(false)}>Cancel</Button>
              <Button onClick={addRound} data-testid="round-save-btn">Add Round</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Close Dialog */}
        <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="close-neg-dialog">
            <DialogHeader><DialogTitle>Close Negotiation</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-3 gap-2">
                {[{v: "won", icon: Trophy, color: "text-emerald-600"}, {v: "lost", icon: XCircle, color: "text-red-600"}, {v: "stalled", icon: Pause, color: "text-amber-600"}].map(o => (
                  <button key={o.v} className={`p-3 rounded-lg border text-center ${closeOutcome === o.v ? "ring-2 ring-blue-400 bg-blue-50" : "hover:bg-slate-50"}`}
                    onClick={() => setCloseOutcome(o.v)} data-testid={`close-${o.v}`}>
                    <o.icon className={`w-5 h-5 mx-auto mb-1 ${o.color}`} /><span className="text-xs capitalize">{o.v}</span>
                  </button>
                ))}
              </div>
              {closeOutcome === "won" && (
                <div><label className="text-sm font-medium mb-1 block">Final Price</label><Input type="number" value={closeFinalPrice} onChange={(e) => setCloseFinalPrice(e.target.value)} /></div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
              <Button onClick={closeNegotiation} disabled={!closeOutcome} data-testid="close-confirm-btn">Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Strategy Dialog */}
        <Dialog open={strategyOpen} onOpenChange={setStrategyOpen}>
          <DialogContent className="sm:max-w-md" data-testid="strategy-dialog">
            <DialogHeader><DialogTitle>AI Negotiation Strategy</DialogTitle></DialogHeader>
            {strategyLoading ? <div className="py-8 text-center text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /><p>Analyzing...</p></div> : strategy ? (
              <div className="space-y-3 py-2">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-sm text-slate-800">{strategy.strategy}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-slate-500">Recommended Price</p><p className="font-bold text-slate-900">{formatINR(strategy.recommended_price)}</p></div>
                  <div><p className="text-xs text-slate-500">Max Discount</p><p className="font-bold text-red-600">{strategy.max_discount_pct}%</p></div>
                  <div><p className="text-xs text-slate-500">Closing Probability</p><p className="font-bold text-emerald-600">{strategy.closing_probability_pct}%</p></div>
                  <div><p className="text-xs text-slate-500">Risk</p><Badge className={`${strategy.risk_level === "low" ? "bg-emerald-100 text-emerald-700" : strategy.risk_level === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{strategy.risk_level}</Badge></div>
                </div>
                {strategy.suggested_freebies?.length > 0 && (
                  <div><p className="text-xs text-slate-500 mb-1">Suggested Freebies</p>
                    <div className="flex flex-wrap gap-1">{strategy.suggested_freebies.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}</div>
                  </div>
                )}
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <p className="text-xs font-medium text-emerald-700 mb-1">Next Move</p>
                  <p className="text-sm text-slate-800">{strategy.next_move}</p>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
            <DialogHeader><DialogTitle>Negotiation Details — {detailNeg?.lead_name}</DialogTitle></DialogHeader>
            {detailNeg && (
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Project:</span> <span className="font-medium">{detailNeg.project_name}</span></div>
                  <div><span className="text-slate-500">Plan:</span> <span className="font-medium">{detailNeg.payment_plan || "N/A"}</span></div>
                  <div><span className="text-slate-500">Quoted:</span> <span className="font-medium">{formatINR(detailNeg.initial_quoted_price)}</span></div>
                  <div><span className="text-slate-500">Current:</span> <span className="font-bold text-slate-900">{formatINR(detailNeg.current_offered_price)}</span></div>
                  <div><span className="text-slate-500">Discount:</span> <span className="text-red-600 font-medium">{detailNeg.total_discount_pct}%</span></div>
                  <div><span className="text-slate-500">Status:</span> <Badge className={`text-xs ${STATUS_STYLES[detailNeg.status]}`}>{detailNeg.status}</Badge></div>
                </div>
                {detailNeg.freebies?.length > 0 && (
                  <div><p className="text-xs text-slate-500 mb-1">Freebies</p><div className="flex flex-wrap gap-1">{detailNeg.freebies.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}</div></div>
                )}
                {detailNeg.rounds?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Rounds ({detailNeg.rounds.length})</p>
                    {detailNeg.rounds.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg mb-1 text-sm">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">{r.round_number}</div>
                        <div className="flex-1">
                          <p className="text-slate-700">Offered: <span className="font-medium">{formatINR(r.offered_price)}</span> ({r.discount_pct}% off)</p>
                          {r.notes && <p className="text-xs text-slate-500">{r.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Negotiation Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-neg-dialog">
            <DialogHeader><DialogTitle>Edit Negotiation</DialogTitle></DialogHeader>
            {editForm && (
              <div className="space-y-3 py-2">
                <div><label className="text-sm font-medium mb-1 block">Project</label><Input value={editForm.project_name || ""} onChange={e => setEditForm({...editForm, project_name: e.target.value})} /></div>
                <div><label className="text-sm font-medium mb-1 block">Payment Plan</label>
                  <Select value={editForm.payment_plan || ""} onValueChange={v => setEditForm({...editForm, payment_plan: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="CLP">CLP</SelectItem><SelectItem value="TLP">TLP</SelectItem><SelectItem value="FLP">FLP</SelectItem><SelectItem value="Subvention">Subvention</SelectItem></SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={editForm.notes || ""} onChange={e => setEditForm({...editForm, notes: e.target.value})} rows={3} /></div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={saveEditNeg} data-testid="save-edit-neg-btn">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Request Discount Approval Dialog */}
        <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="request-approval-dialog">
            <DialogHeader><DialogTitle>Request Discount Approval</DialogTitle></DialogHeader>
            {approvalNeg && (
              <div className="space-y-4 py-2">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Approval Required</span>
                  </div>
                  <p className="text-xs text-amber-700">
                    Discounts above the threshold require manager approval before the deal can be closed as won.
                  </p>
                </div>

                <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Lead:</span>
                    <span className="font-medium">{approvalNeg.lead_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Project:</span>
                    <span className="font-medium">{approvalNeg.project_name || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Original Price:</span>
                    <span className="font-medium">{formatINR(approvalNeg.initial_quoted_price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Current Offer:</span>
                    <span className="font-medium text-red-600">{formatINR(approvalNeg.current_offered_price)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-slate-500">Discount:</span>
                    <span className="font-bold text-red-600">{approvalNeg.total_discount_pct}%</span>
                  </div>
                </div>

                {approvalNeg.freebies?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Freebies included:</p>
                    <div className="flex flex-wrap gap-1">
                      {approvalNeg.freebies.map((f, i) => <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>)}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium mb-1 block">Justification</label>
                  <Textarea 
                    value={approvalJustification} 
                    onChange={(e) => setApprovalJustification(e.target.value)} 
                    rows={3} 
                    placeholder="Why is this discount necessary? e.g., competitive pricing, ready buyer, bulk deal..."
                    data-testid="approval-justification"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovalOpen(false)}>Cancel</Button>
              <Button onClick={requestDiscountApproval} className="bg-amber-600 hover:bg-amber-700" data-testid="submit-approval-request-btn">
                Submit for Approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default NegotiationPage;
